/**
 * Documentos de um pedido da rota: o Pedido de Venda impresso (sem valor fiscal) e a situação
 * fiscal — com a emissão POSTERIOR da NF-e pelo MESMO pedido (sem novo pedido, sem mexer de novo
 * em estoque ou financeiro: a nota só documenta a venda já confirmada).
 *
 * Imprimir nunca altera a venda nem a nota: falha da impressora só é avisada.
 */
import {
  layoutRows,
  type FiscalDocumentDetail,
  type FiscalLink,
  type FiscalPreview,
  type PrintLayout,
  type SalesOrder,
} from "@cafegestor/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  ApiRequestError,
  FISCAL_KEY,
  apiFetch,
  errorMessage,
  newIdempotencyKey,
} from "@cafegestor/client";
import {
  TRANSPORT_LABEL,
  availableTransports,
  preferredTransport,
  printLayout,
  savePreferredTransport,
  type Transport,
} from "@cafegestor/client/printing";
import { Alert, Button, Icon, Select, StatusBadge } from "@cafegestor/ui";
import { useRotaHost } from "./host";
import { orderFiscalState, orderPrintLayout } from "./rota-lib";

// ------------------------------------------------------------------ visualização
/** Como vai sair na bobina (48 colunas), para conferir antes de imprimir. */
export function PrintPreview({ layout }: { layout: PrintLayout }) {
  const rows = layoutRows(layout);
  return (
    <div
      aria-label={`Visualização: ${layout.title}`}
      className="overflow-x-auto rounded-xl border border-border bg-white p-3 shadow-inner"
    >
      <pre className="mx-auto w-max font-mono text-[11px] leading-[1.35] whitespace-pre-wrap text-black min-[400px]:text-[12px]">
        {rows.map((r, i) => (
          <span key={i} className={r.bold ? "font-bold" : ""}>
            {r.text}
            {"\n"}
          </span>
        ))}
      </pre>
    </div>
  );
}

const printedKey = (id: string) => `cafegestor.rota.pedidoImpresso.${id}`;
function wasPrinted(id: string) {
  try {
    return sessionStorage.getItem(printedKey(id)) === "1";
  } catch {
    return false;
  }
}

/** Pedido de Venda: visualizar, imprimir e reimprimir (sem valor fiscal). */
export function OrderPrintCard({ order }: { order: SalesOrder }) {
  const { companyName, printer } = useRotaHost();
  // o app escolhe os meios (no Android, compartilhar pelo sistema); padrão: os do navegador
  const options = printer?.options ?? availableTransports();
  const send = printer?.print ?? printLayout;
  const [transport, setTransport] = useState<Transport>(() => preferredTransport(options));
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [printed, setPrinted] = useState(() => wasPrinted(order.id));
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const sharing = transport === "compartilhar";
  const layout = orderPrintLayout(order, {
    companyName,
    reprint: printed,
    printedAt: new Date(),
  });

  async function print() {
    setBusy(true);
    setMessage(null);
    savePreferredTransport(transport);
    try {
      await send(layout, transport);
      if (sharing) {
        // compartilhar não é impressão: não marca a próxima impressão como reimpressão
        setMessage({ tone: "ok", text: "Pedido de Venda pronto para compartilhar." });
        return;
      }
      try {
        sessionStorage.setItem(printedKey(order.id), "1");
      } catch {
        /* sem armazenamento */
      }
      setMessage({
        tone: "ok",
        text: printed ? "Reimpressão enviada." : "Pedido enviado para a impressora.",
      });
      setPrinted(true);
    } catch (err) {
      setMessage({
        tone: "error",
        text: sharing
          ? `Não foi possível compartilhar: ${errorMessage(err)}. A venda continua registrada.`
          : `A impressora falhou: ${errorMessage(err)}. A venda continua registrada — confira a impressora e tente de novo, ou use outro meio.`,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-label="Pedido de Venda"
      className="space-y-3 rounded-2xl border border-border bg-card p-4 shadow-card"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-caramel-soft text-accent"
        >
          <Icon name="receipt" size={20} />
        </span>
        <div className="min-w-0">
          <h3 className="font-semibold">Pedido de Venda {order.number}</h3>
          <p className="text-[13px] text-muted-foreground">
            Documento comercial <strong>sem valor fiscal</strong> — não substitui a nota fiscal.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-[1fr_auto]">
        <Select
          aria-label="Meio de impressão"
          className="h-12"
          value={transport}
          onChange={(e) => setTransport(e.target.value as Transport)}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {TRANSPORT_LABEL[o]}
            </option>
          ))}
        </Select>
        <Button size="lg" onClick={() => void print()} disabled={busy}>
          <Icon name={sharing ? "share" : "printer"} size={20} />
          {sharing
            ? busy
              ? "Abrindo..."
              : "Compartilhar pedido"
            : busy
              ? "Imprimindo..."
              : printed
                ? "Reimprimir pedido"
                : "Imprimir pedido"}
        </Button>
      </div>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      <Button
        variant="ghost"
        className="w-full"
        aria-expanded={showPreview}
        onClick={() => setShowPreview((v) => !v)}
      >
        {showPreview ? "Ocultar visualização" : "Visualizar pedido"}
      </Button>
      {showPreview && <PrintPreview layout={layout} />}
    </section>
  );
}

// ------------------------------------------------------------------ situação fiscal
// O acompanhamento da nota (cartão com etapas) é do app hospedeiro: ver RotaHost.
/**
 * Situação fiscal do pedido e emissão posterior da NF-e (pelo mesmo pedido). A conferência é a
 * prévia do servidor; a emissão exige o hash dela e uma chave de idempotência (clique repetido
 * ou conexão perdida não criam outra nota — e o banco só admite uma nota ativa por pedido).
 */
export function OrderFiscalCard({ order }: { order: SalesOrder }) {
  const { can, EmissionTracking } = useRotaHost();
  const queryClient = useQueryClient();
  const linksPath = `/origem/venda/${order.id}`;
  const links = useQuery({
    queryKey: [...FISCAL_KEY, linksPath],
    queryFn: () => apiFetch<FiscalLink[]>(`/fiscal${linksPath}`),
    enabled: can("fiscal.ver"),
  });
  const [preview, setPreview] = useState<FiscalPreview | null>(null);
  const [busy, setBusy] = useState<"previa" | "emitir" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [key, setKey] = useState(newIdempotencyKey);
  const [since] = useState(() => new Date().toISOString());
  const state = orderFiscalState(order, links.data);

  async function conferir() {
    setBusy("previa");
    setError(null);
    try {
      setPreview(
        await apiFetch<FiscalPreview>("/fiscal/previa", {
          method: "POST",
          body: { use: "venda_rota", sourceId: order.id, model: null },
        }),
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function emitir() {
    if (!preview || busy) return;
    setBusy("emitir");
    setError(null);
    try {
      await apiFetch<FiscalDocumentDetail>("/fiscal/emitir", {
        method: "POST",
        body: {
          use: "venda_rota",
          sourceId: order.id,
          previewHash: preview.hash,
          model: preview.calc.model,
        },
        idempotencyKey: key,
      });
      setPreview(null);
      setKey(newIdempotencyKey());
      await queryClient.invalidateQueries({ queryKey: FISCAL_KEY });
    } catch (err) {
      // cálculo mudou: a API devolve a prévia nova para conferir de novo
      if (err instanceof ApiRequestError && err.code === "PREVIA_DESATUALIZADA") {
        const fresh = (err.details as { preview?: FiscalPreview } | undefined)?.preview;
        if (fresh) setPreview(fresh);
      }
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  if (!can("fiscal.ver")) return null;
  return (
    <section aria-label="Nota fiscal do pedido" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">Nota fiscal</h3>
        {links.data && <StatusBadge tone={state.tone}>{state.label}</StatusBadge>}
      </div>
      {links.isPending && (
        <p role="status" className="text-sm text-muted-foreground">
          Conferindo se o pedido já tem nota…
        </p>
      )}
      {state.documentId && <EmissionTracking documentId={state.documentId} since={since} />}
      {links.data && state.canEmit && (
        <div className="space-y-3 rounded-2xl border border-warning/30 bg-warning-soft p-4 text-warning-strong">
          <p className="text-sm">
            Este pedido ainda <strong>não tem nota fiscal</strong>. Quando a operação exigir, emita
            a NF-e pelo mesmo pedido: estoque e financeiro não se repetem.
          </p>
          {can("fiscal.documento.emitir") ? (
            !preview ? (
              <Button
                size="lg"
                className="w-full"
                onClick={() => void conferir()}
                disabled={busy !== null}
              >
                <Icon name="fiscal" size={20} />
                {busy === "previa" ? "Conferindo..." : "Emitir NF-e deste pedido"}
              </Button>
            ) : preview.calc.issues.length > 0 ? (
              <Alert tone="error">
                <strong>A nota não pode ser emitida agora:</strong>
                <ul className="mt-1 list-disc pl-5">
                  {preview.calc.issues.map((i, n) => (
                    <li key={`${i.code}-${n}`}>{i.message}</li>
                  ))}
                </ul>
              </Alert>
            ) : (
              <div className="space-y-2 text-foreground">
                <p className="text-sm">
                  Nota conferida: {preview.calc.model === "65" ? "NFC-e" : "NF-e"} para{" "}
                  <strong>{preview.recipient.name}</strong>. Confirmar a emissão?
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => setPreview(null)}
                    disabled={busy !== null}
                  >
                    Voltar
                  </Button>
                  <Button size="lg" onClick={() => void emitir()} disabled={busy !== null}>
                    {busy === "emitir" ? "Emitindo..." : "Confirmar emissão"}
                  </Button>
                </div>
              </div>
            )
          ) : (
            <p className="text-sm">
              Você não tem permissão para emitir a nota: avise o escritório.
            </p>
          )}
        </div>
      )}
      {error && <Alert tone="error">{error}</Alert>}
      {links.error !== null && <Alert tone="error">{errorMessage(links.error)}</Alert>}
    </section>
  );
}

/** Pedido completo (lido do servidor): impressão e nota. */
export function OrderDocuments({ orderId }: { orderId: string }) {
  const order = useQuery({
    queryKey: ["vendas", "pedidos", orderId],
    queryFn: () => apiFetch<SalesOrder>(`/vendas/pedidos/${orderId}`),
  });
  if (order.error) return <Alert tone="error">{errorMessage(order.error)}</Alert>;
  if (!order.data)
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Carregando o pedido…
      </p>
    );
  return (
    <div className="space-y-4">
      <OrderPrintCard order={order.data} />
      <OrderFiscalCard order={order.data} />
    </div>
  );
}
